import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const API_BASE_DEFAULT = process.env.REACT_APP_API_BASE || '';
const API_KEY_DEFAULT = process.env.REACT_APP_API_KEY || '';
const API_SECRET_DEFAULT = process.env.REACT_APP_API_SECRET || '';

export const STORAGE_API_BASE = 'atlas-ide:v2:api-base';
export const STORAGE_API_KEY = 'atlas-ide:v2:api-key';
export const STORAGE_API_SECRET = 'atlas-ide:v2:api-secret';
export const STORAGE_THEME = 'atlas-ide:v2:theme';
export const STORAGE_AUTOSAVE = 'atlas-ide:v2:autosave';
export const STORAGE_MINIMAP = 'atlas-ide:v2:minimap';
export const STORAGE_VIM_MODE = 'atlas-ide:v2:vim-mode';
export const STORAGE_BLAME = 'atlas-ide:v2:blame';
export const STORAGE_FONT_SIZE = 'atlas-ide:v2:font-size';
export const STORAGE_REDUCED_MOTION = 'atlas-ide:v2:reduced-motion';
export const STORAGE_HIGH_CONTRAST = 'atlas-ide:v2:high-contrast';
export const STORAGE_COLOR_BLIND_MODE = 'atlas-ide:v2:color-blind-mode';

type Theme = 'dark' | 'light';
type ColorBlindMode = 'none' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia';

type SettingsContextValue = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  apiBase: string;
  setApiBase: (base: string) => void;
  apiKey: string;
  setApiKey: (key: string) => void;
  apiSecret: string;
  setApiSecret: (secret: string) => void;
  autoSaveEnabled: boolean;
  setAutoSaveEnabled: (enabled: boolean) => void;
  indentSize: number;
  setIndentSize: (size: number) => void;
  encoding: string;
  setEncoding: (encoding: string) => void;
  minimapEnabled: boolean;
  setMinimapEnabled: (enabled: boolean) => void;
  vimEnabled: boolean;
  setVimEnabled: (enabled: boolean) => void;
  blameEnabled: boolean;
  setBlameEnabled: (enabled: boolean) => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
  reducedMotion: boolean;
  setReducedMotion: (enabled: boolean) => void;
  toggleReducedMotion: () => void;
  highContrast: boolean;
  setHighContrast: (enabled: boolean) => void;
  toggleHighContrast: () => void;
  colorBlindMode: ColorBlindMode;
  setColorBlindMode: (mode: ColorBlindMode) => void;
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem(STORAGE_THEME);
    return saved === 'light' ? 'light' : 'dark';
  });

  const [apiBase, setApiBaseState] = useState<string>(
    () => localStorage.getItem(STORAGE_API_BASE) || API_BASE_DEFAULT
  );
  const [apiKey, setApiKeyState] = useState<string>(
    () => localStorage.getItem(STORAGE_API_KEY) || API_KEY_DEFAULT
  );
  const [apiSecret, setApiSecretState] = useState<string>(
    () => localStorage.getItem(STORAGE_API_SECRET) || API_SECRET_DEFAULT
  );

  const [autoSaveEnabled, setAutoSaveEnabledState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_AUTOSAVE);
    return saved !== 'false';
  });

  const [indentSize, setIndentSize] = useState<number>(2);
  const [encoding, setEncoding] = useState<string>('UTF-8');
  const [minimapEnabled, setMinimapEnabledState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_MINIMAP);
    return saved === 'true';
  });

  const [vimEnabled, setVimEnabledState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_VIM_MODE);
    return saved === 'true';
  });

  const [blameEnabled, setBlameEnabledState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_BLAME);
    return saved === 'true';
  });

  const [fontSize, setFontSizeState] = useState<number>(() => {
    const saved = localStorage.getItem(STORAGE_FONT_SIZE);
    const parsed = saved ? parseInt(saved, 10) : 14;
    return isNaN(parsed) ? 14 : Math.min(Math.max(parsed, 8), 32);
  });

  const [reducedMotion, setReducedMotionState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_REDUCED_MOTION);
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to system preference
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  const [highContrast, setHighContrastState] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_HIGH_CONTRAST);
    if (saved !== null) {
      return saved === 'true';
    }
    // Default to system preference
    return window.matchMedia('(prefers-contrast: more)').matches;
  });

  const [colorBlindMode, setColorBlindModeState] = useState<ColorBlindMode>(() => {
    const saved = localStorage.getItem(STORAGE_COLOR_BLIND_MODE);
    if (saved && ['none', 'protanopia', 'deuteranopia', 'tritanopia', 'achromatopsia'].includes(saved)) {
      return saved as ColorBlindMode;
    }
    return 'none';
  });

  const apiBaseInitRef = useRef(false);
  const credsInitRef = useRef(false);

  // Persist theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_THEME, theme);
  }, [theme]);

  // Persist API base
  useEffect(() => {
    if (!apiBaseInitRef.current) {
      apiBaseInitRef.current = true;
      return;
    }
    localStorage.setItem(STORAGE_API_BASE, apiBase);
  }, [apiBase]);

  // Persist credentials
  useEffect(() => {
    if (!credsInitRef.current) {
      credsInitRef.current = true;
      return;
    }
    localStorage.setItem(STORAGE_API_KEY, apiKey);
    localStorage.setItem(STORAGE_API_SECRET, apiSecret);
  }, [apiKey, apiSecret]);

  // Persist autosave
  useEffect(() => {
    localStorage.setItem(STORAGE_AUTOSAVE, String(autoSaveEnabled));
  }, [autoSaveEnabled]);

  // Persist minimap
  useEffect(() => {
    localStorage.setItem(STORAGE_MINIMAP, String(minimapEnabled));
  }, [minimapEnabled]);

  // Persist vim mode
  useEffect(() => {
    localStorage.setItem(STORAGE_VIM_MODE, String(vimEnabled));
  }, [vimEnabled]);

  // Persist blame
  useEffect(() => {
    localStorage.setItem(STORAGE_BLAME, String(blameEnabled));
  }, [blameEnabled]);

  // Persist font size
  useEffect(() => {
    localStorage.setItem(STORAGE_FONT_SIZE, String(fontSize));
  }, [fontSize]);

  // Persist reduced motion and apply to document
  useEffect(() => {
    localStorage.setItem(STORAGE_REDUCED_MOTION, String(reducedMotion));
    document.documentElement.setAttribute('data-reduced-motion', String(reducedMotion));
  }, [reducedMotion]);

  // Persist high contrast and apply to document
  useEffect(() => {
    localStorage.setItem(STORAGE_HIGH_CONTRAST, String(highContrast));
    document.documentElement.setAttribute('data-high-contrast', String(highContrast));
  }, [highContrast]);

  // Persist color blind mode and apply to document
  useEffect(() => {
    localStorage.setItem(STORAGE_COLOR_BLIND_MODE, colorBlindMode);
    document.documentElement.setAttribute('data-color-blind-mode', colorBlindMode);
  }, [colorBlindMode]);

  const setTheme = useCallback((t: Theme) => setThemeState(t), []);
  const setMinimapEnabled = useCallback((e: boolean) => setMinimapEnabledState(e), []);
  const setVimEnabled = useCallback((e: boolean) => setVimEnabledState(e), []);
  const setBlameEnabled = useCallback((e: boolean) => setBlameEnabledState(e), []);
  const setApiBase = useCallback((b: string) => setApiBaseState(b), []);
  const setApiKey = useCallback((k: string) => setApiKeyState(k), []);
  const setApiSecret = useCallback((s: string) => setApiSecretState(s), []);
  const setAutoSaveEnabled = useCallback((e: boolean) => setAutoSaveEnabledState(e), []);
  const setFontSize = useCallback((size: number) => {
    const clamped = Math.min(Math.max(size, 8), 32);
    setFontSizeState(clamped);
  }, []);
  const increaseFontSize = useCallback(() => {
    setFontSizeState((prev) => Math.min(prev + 1, 32));
  }, []);
  const decreaseFontSize = useCallback(() => {
    setFontSizeState((prev) => Math.max(prev - 1, 8));
  }, []);
  const resetFontSize = useCallback(() => {
    setFontSizeState(14);
  }, []);
  const setReducedMotion = useCallback((e: boolean) => setReducedMotionState(e), []);
  const toggleReducedMotion = useCallback(() => {
    setReducedMotionState((prev) => !prev);
  }, []);
  const setHighContrast = useCallback((e: boolean) => setHighContrastState(e), []);
  const toggleHighContrast = useCallback(() => {
    setHighContrastState((prev) => !prev);
  }, []);
  const setColorBlindMode = useCallback((mode: ColorBlindMode) => setColorBlindModeState(mode), []);

  const value = useMemo<SettingsContextValue>(
    () => ({
      theme,
      setTheme,
      apiBase,
      setApiBase,
      apiKey,
      setApiKey,
      apiSecret,
      setApiSecret,
      autoSaveEnabled,
      setAutoSaveEnabled,
      indentSize,
      setIndentSize,
      encoding,
      setEncoding,
      minimapEnabled,
      setMinimapEnabled,
      vimEnabled,
      setVimEnabled,
      blameEnabled,
      setBlameEnabled,
      fontSize,
      setFontSize,
      increaseFontSize,
      decreaseFontSize,
      resetFontSize,
      reducedMotion,
      setReducedMotion,
      toggleReducedMotion,
      highContrast,
      setHighContrast,
      toggleHighContrast,
      colorBlindMode,
      setColorBlindMode,
    }),
    [
      theme,
      setTheme,
      apiBase,
      setApiBase,
      apiKey,
      setApiKey,
      apiSecret,
      setApiSecret,
      autoSaveEnabled,
      setAutoSaveEnabled,
      indentSize,
      encoding,
      minimapEnabled,
      setMinimapEnabled,
      vimEnabled,
      setVimEnabled,
      blameEnabled,
      setBlameEnabled,
      fontSize,
      setFontSize,
      increaseFontSize,
      decreaseFontSize,
      resetFontSize,
      reducedMotion,
      setReducedMotion,
      toggleReducedMotion,
      highContrast,
      setHighContrast,
      toggleHighContrast,
      colorBlindMode,
      setColorBlindMode,
    ]
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
